// Client/src/pages/digi/NewMasterSheet/RowDetailPopover.jsx
//
// The (i) detail for a computed cell — one component, four bodies (spec 2026-10-14, choice A:
// a card anchored to the cell). The caller owns which cell is open; this draws it, closes on
// click-away / Esc, and asks for a document's signed URL only when Download is clicked.
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { toast } from "react-toastify";
import { insuranceLabel, shortDate } from "./rowExtras";
import { getDocumentsByUnitNo } from "./TenantForm/Helpers/api";
import "./rowDetailPopover.css";

const money = (n) => (n === null || n === undefined ? "—" : `$${Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const kb = (n) => (n ? `${(n / 1024).toFixed(0)} KB` : "");

const Pill = ({ tone, children }) => <span className={`rx-pill rx-pill--${tone}`}>{children}</span>;

const InsuranceBody = ({ x, unit, propertyId, buildingName, onEdit }) => {
  const [urls, setUrls] = useState(null); // doc id → signedUrl, fetched on first Download
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);
  const label = insuranceLabel(x);
  const download = async (doc) => {
    // Open the tab synchronously (inside the click) so popup blockers allow it.
    const w = window.open("", "_blank");
    if (w) w.opener = null;
    const fail = () => {
      if (w && !w.closed) w.close();
      toast.error("Couldn't open the file");
    };
    try {
      let map = urls;
      if (!map) {
        const res = await getDocumentsByUnitNo(propertyId, buildingName, unit);
        const docs = res?.data?.documents || [];
        map = Object.fromEntries(docs.map((d) => [String(d._id), d.signedUrl]).filter(([, u]) => u));
        if (mounted.current) setUrls(map);
      }
      const url = map[doc.id];
      if (!url) return fail();
      if (w && !w.closed) w.location.href = url;
      else window.open(url, "_blank", "noopener");
    } catch (e) {
      fail();
    }
  };
  return (
    <>
      <h4 id="rx-pop-title">Insurance — Unit {unit} <Pill tone={label.tone}>{x.daysUntil !== null && x.status === "EXPIRING" ? `Expiring in ${x.daysUntil} days` : label.text}</Pill></h4>
      <dl>
        <dt>Provider</dt><dd>{x.provider || <span className="rx-muted">—</span>}</dd>
        <dt>Policy no.</dt><dd>{x.policyNo || <span className="rx-muted">—</span>}</dd>
        <dt>Insured</dt><dd>{x.insured || <span className="rx-muted">—</span>}</dd>
        <dt>Expires</dt><dd>{x.expiry ? shortDate(x.expiry) : <span className="rx-muted">—</span>}</dd>
        <dt>Reviewed</dt>
        <dd>{x.reviewed || "No"}{x.reviewedBy ? <span className="rx-muted"> · {x.reviewedBy}{x.reviewedAt ? `, ${shortDate(x.reviewedAt)}` : ""}</span> : null}</dd>
      </dl>
      <div className="rx-list">
        {x.na && !x.documents.length && <div className="rx-muted">Marked N/A on the Documents tab</div>}
        {!x.na && !x.documents.length && <div className="rx-pill rx-pill--none">No policy uploaded</div>}
        {x.documents.map((d) => (
          <div className="rx-row" key={d.id}>
            <span>{d.name} <span className="rx-muted">{[kb(d.size), d.uploadedAt ? shortDate(d.uploadedAt) : ""].filter(Boolean).join(" · ")}</span></span>
            <button type="button" className="rx-link" onClick={() => download(d)}>Download</button>
          </div>
        ))}
      </div>
      <div className="rx-foot"><button type="button" className="rx-btn" onClick={onEdit}>Open Edit Tenant → Insurance</button></div>
    </>
  );
};

const AddendumsBody = ({ x, unit }) => (
  <>
    <h4 id="rx-pop-title">Addendums — Unit {unit} <span className="rx-muted">{x.count} current</span></h4>
    <div className="rx-list">
      {!x.items.length && <div className="rx-muted">No addendums for this unit</div>}
      {x.items.map((a) => (
        <div className="rx-row" key={a.id}>
          <span>{a.summary}<br /><span className="rx-muted">{a.effectiveDate ? shortDate(a.effectiveDate) : "no date"} · {a.status}{a.current ? " · current" : ""}</span></span>
          {a.hasSignedPdf && <span className="rx-muted">signed PDF</span>}
        </div>
      ))}
    </div>
  </>
);

const IncentivesBody = ({ x, unit }) => (
  <>
    <h4 id="rx-pop-title">Incentives — Unit {unit}</h4>
    <div className="rx-list">
      {!x.items.length && <div className="rx-muted">No PAD incentives recorded</div>}
      {x.items.map((i, n) => (
        <div className="rx-row" key={`${i.padId}-${i.date}-${n}`}>
          <span>{shortDate(i.date)} <span className="rx-muted">({i.month})</span><br /><span className="rx-muted">{i.reason || "no reason given"}{i.by ? ` · ${i.by}` : ""}{i.at ? `, ${shortDate(i.at)}` : ""}</span></span>
          <strong>−{money(i.amount)}</strong>
        </div>
      ))}
    </div>
  </>
);

const FutureLeaseBody = ({ x, unit }) => (
  <>
    <h4 id="rx-pop-title">Future lease — Unit {unit} <Pill tone={x.signed ? "ok" : "soon"}>{x.signed ? "Signed" : "Awaiting signatures"}</Pill></h4>
    <dl>
      <dt>Kind</dt><dd>{x.kind === "renewal" ? "Renewal" : "New lease"}</dd>
      <dt>Name</dt><dd>{x.name || "—"}</dd>
      <dt>Term</dt><dd>{x.start ? shortDate(x.start) : "—"}{x.end ? ` → ${shortDate(x.end)}` : ""}</dd>
      <dt>Refundable deposit</dt><dd>{money(x.deposit)}</dd>
      <dt>Basic rent</dt><dd>{money(x.basicRent)}</dd>
      <dt>Total rent</dt><dd>{money(x.totalRent)}</dd>
      <dt>Incentives</dt><dd>{x.incentives || <span className="rx-muted">—</span>}</dd>
    </dl>
  </>
);

/**
 * props: columnKey (__x_insurance | __x_addendums | __x_incentives | __x_futureLease),
 * extras (the row's RowExtras), row (the unit row), propertyId, buildingName, anchorRef
 * (the (i) button), onClose, onEditTenant(row, section).
 */
const RowDetailPopover = ({ columnKey, extras, row, propertyId, buildingName, anchorRef, onClose, onEditTenant }) => {
  const ref = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  useLayoutEffect(() => {
    const place = () => {
      const a = anchorRef?.current;
      if (!a) return;
      const r = a.getBoundingClientRect();
      const h = ref.current ? ref.current.offsetHeight : 320;
      const left = Math.max(8, Math.min(r.left, window.innerWidth - 332));
      let top = r.bottom + 6;
      if (top + h > window.innerHeight) top = Math.max(8, r.top - 6 - h);
      setPos({ top, left });
    };
    place();
    window.addEventListener("resize", place);
    return () => window.removeEventListener("resize", place);
  }, [anchorRef, columnKey]);

  useEffect(() => {
    const anchor = anchorRef?.current;
    if (ref.current) ref.current.focus();
    return () => { if (anchor && document.contains(anchor)) anchor.focus(); };
  }, [anchorRef]);

  useEffect(() => {
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target) && !(anchorRef?.current && anchorRef.current.contains(e.target))) onClose();
    };
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    const onScroll = (e) => { if (!ref.current || !ref.current.contains(e.target)) onClose(); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    document.addEventListener("scroll", onScroll, true);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("scroll", onScroll, true);
    };
  }, [anchorRef, onClose]);

  const unit = String(row?.unitNo ?? "");
  let body = null;
  if (columnKey === "__x_insurance") body = <InsuranceBody x={extras.insurance} unit={unit} propertyId={propertyId} buildingName={buildingName} onEdit={() => { onClose(); onEditTenant(row, "insurance"); }} />;
  else if (columnKey === "__x_addendums") body = <AddendumsBody x={extras.addendums} unit={unit} />;
  else if (columnKey === "__x_incentives") body = <IncentivesBody x={extras.incentives} unit={unit} />;
  else if (columnKey === "__x_futureLease" && extras.futureLease) body = <FutureLeaseBody x={extras.futureLease} unit={unit} />;
  if (!body) return null;

  return ReactDOM.createPortal(
    <div ref={ref} className="rx-pop" role="dialog" aria-labelledby="rx-pop-title" tabIndex={-1} style={{ top: pos.top, left: pos.left }} onClick={(e) => e.stopPropagation()}>
      {body}
    </div>,
    document.body
  );
};

export default RowDetailPopover;
